import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import ModeratorLayout from "@/components/ModeratorLayout/ModeratorLayout";
import { unwrapPaginated } from "@/config/api";
import i18n from "@/i18n";
import { EMPTY_VALUE, formatDateTime, formatNumber } from "@/i18n/format";
import type { MatchingRunListItem } from "@/services/moderators/generated/model";
import {
  useModeratorsMatchingRunsList,
  useModeratorsMatchingRunsStats,
  useModeratorsMatchingScores,
} from "@/services/moderators/generated/moderators-matching/moderators-matching";
import "./ModeratorPage.css";
import "./ModeratorMatchingPage.css";
import "./ModeratorMatchingRunsPage.css";

type StatusFilter = "" | "pending" | "working" | "completed" | "failed";

const STATUS_OPTIONS = [
  { value: "", labelKey: "runs.list.statusOption.all" },
  { value: "completed", labelKey: "runs.list.statusOption.completed" },
  { value: "failed", labelKey: "runs.list.statusOption.failed" },
  { value: "working", labelKey: "runs.list.statusOption.working" },
  { value: "pending", labelKey: "runs.list.statusOption.pending" },
] as const satisfies ReadonlyArray<{ value: StatusFilter; labelKey: string }>;

const STAGES = [
  "started",
  "query_embedded",
  "vector_search",
  "ranking",
  "threshold_filter",
  "persisted",
  "completed",
  "failed",
] as const;
type Stage = (typeof STAGES)[number];
const isStage = (s: string): s is Stage => (STAGES as readonly string[]).includes(s);

const stageLabel = (stage: string): string => (isStage(stage) ? i18n.t(`deals:pipeline.stage.${stage}`) : stage);

const fmtDuration = (ms: number | null | undefined): string => {
  if (ms == null) return EMPTY_VALUE;
  if (ms < 1000) return i18n.t("deals:pipeline.durationMs", { value: formatNumber(ms) });
  return i18n.t("deals:pipeline.durationS", {
    value: formatNumber(ms / 1000, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
  });
};

const fmtDate = (iso: string | null | undefined): string => formatDateTime(iso);

const fixed = (n: number, digits: number): string =>
  formatNumber(n, { minimumFractionDigits: digits, maximumFractionDigits: digits });

const ModeratorMatchingRunsPage = () => (
  <ModeratorLayout>{(moderator) => <RunsContent isSuper={moderator.role === "SUPERMODERATOR"} />}</ModeratorLayout>
);

const RunsContent = ({ isSuper }: { isSuper: boolean }) => {
  const { t } = useTranslation(["deals", "common"]);
  const [statusInput, setStatusInput] = useState<StatusFilter>("");
  const [companyIdInput, setCompanyIdInput] = useState("");
  const [appliedFilters, setAppliedFilters] = useState<{
    status?: string;
    companyId?: number;
  }>({});

  const queryParams = useMemo(() => {
    const p: { status?: string; companyId?: number } = {};
    if (appliedFilters.status) p.status = appliedFilters.status;
    if (appliedFilters.companyId) p.companyId = appliedFilters.companyId;
    return Object.keys(p).length ? p : undefined;
  }, [appliedFilters]);

  const { data, isLoading, error, refetch, isFetching } = useModeratorsMatchingRunsList(queryParams, {
    query: { enabled: isSuper },
  });
  const runs = unwrapPaginated<MatchingRunListItem>(data);

  const { data: stats } = useModeratorsMatchingRunsStats({ query: { enabled: isSuper } });
  const { data: scores } = useModeratorsMatchingScores(undefined, { query: { enabled: isSuper } });
  const maxBucket = Math.max(1, ...(scores?.buckets ?? []).map((b) => b.count));

  if (!isSuper) {
    return (
      <>
        <header className="mod-page__header">
          <div>
            <h1 className="mod-page__title">{t("runs.title")}</h1>
            <p className="mod-page__subtitle">{t("shared.superOnly")}</p>
          </div>
        </header>
        <div className="mod-page__empty">{t("runs.restricted")}</div>
      </>
    );
  }

  const applyFilters = (e: React.FormEvent) => {
    e.preventDefault();
    const companyId = companyIdInput.trim() ? Number(companyIdInput.trim()) : undefined;
    setAppliedFilters({
      status: statusInput || undefined,
      companyId: Number.isFinite(companyId) ? companyId : undefined,
    });
  };

  const resetFilters = () => {
    setStatusInput("");
    setCompanyIdInput("");
    setAppliedFilters({});
  };

  return (
    <div className="moderator-matching">
      <header className="mod-page__header">
        <div>
          <h1 className="mod-page__title">{t("runs.title")}</h1>
          <p className="mod-page__subtitle">{t("runs.subtitle")}</p>
        </div>
        <div className="moderator-matching-runs__header-actions">
          <Link to="/moderator/matching" className="mod-page__btn mod-page__btn--ghost">
            <i className="fa-solid fa-sliders" /> <span>{t("runs.engineSettings")}</span>
          </Link>
          <button
            type="button"
            className="mod-page__btn mod-page__btn--ghost"
            onClick={() => refetch()}
            disabled={isFetching}
            aria-label={t("runs.refreshAria")}
          >
            <i className={`fa-solid ${isFetching ? "fa-spinner fa-spin" : "fa-arrows-rotate"}`} />
            <span style={{ marginLeft: 6 }}>{t("common:actions.refresh")}</span>
          </button>
        </div>
      </header>

      <section className="moderator-matching__panel">
        <header className="moderator-matching__panel-header">
          <h2>{t("runs.activity.title")}</h2>
          <p className="moderator-matching__hint">{t("runs.activity.hint")}</p>
        </header>
        <div className="moderator-matching-runs__stats-grid">
          <StatsCard
            title={t("runs.activity.last7")}
            total={stats?.last7d?.totalRuns}
            successful={stats?.last7d?.successfulRuns}
            failed={stats?.last7d?.failedRuns}
            avgMs={stats?.last7d?.avgDurationMs}
          />
          <StatsCard
            title={t("runs.activity.last30")}
            total={stats?.last30d?.totalRuns}
            successful={stats?.last30d?.successfulRuns}
            failed={stats?.last30d?.failedRuns}
            avgMs={stats?.last30d?.avgDurationMs}
          />
        </div>
      </section>

      {scores && (
        <section className="moderator-matching__panel">
          <header className="moderator-matching__panel-header">
            <h2>{t("runs.scores.title")}</h2>
            <p className="moderator-matching__hint">
              {t("runs.scores.hint", { days: scores.windowDays, threshold: fixed(scores.threshold.effective, 2) })}
            </p>
          </header>
          <div className="moderator-matching-runs__stats-row">
            <Stat label={t("runs.scores.candidates")} value={scores.totalCandidates} />
            <Stat label={t("runs.scores.aboveMinimum")} value={scores.aboveThreshold} tone="ok" />
            <Stat label={t("runs.scores.envMinimum")} value={fixed(scores.threshold.envMinimum, 2)} />
            <Stat label={t("runs.scores.moderatorMinimum")} value={fixed(scores.threshold.moderatorMinimum, 2)} />
          </div>
          <div className="moderator-matching-runs__histogram" role="img" aria-label={t("runs.scores.histogramAria")}>
            {scores.buckets.map((b) => (
              <div
                key={b.lower}
                className={`moderator-matching-runs__histogram-bar${
                  b.upper <= scores.threshold.effective ? " moderator-matching-runs__histogram-bar--below" : ""
                }`}
                title={t("runs.scores.bucket", { lower: fixed(b.lower, 1), upper: fixed(b.upper, 1), count: b.count })}
              >
                <span style={{ height: `${(b.count / maxBucket) * 100}%` }} />
                <small>{fixed(b.lower, 1)}</small>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="moderator-matching__panel">
        <header className="moderator-matching__panel-header">
          <h2>{t("runs.list.title")}</h2>
          <p className="moderator-matching__hint">{t("runs.list.hint")}</p>
        </header>

        <form className="mod-page__toolbar" onSubmit={applyFilters}>
          <label className="moderator-matching-runs__filter">
            <span>{t("runs.list.status")}</span>
            <select value={statusInput} onChange={(e) => setStatusInput(e.target.value as StatusFilter)}>
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value || "all"} value={opt.value}>
                  {t(opt.labelKey)}
                </option>
              ))}
            </select>
          </label>
          <label className="moderator-matching-runs__filter">
            <span>{t("runs.list.companyId")}</span>
            <input
              type="number"
              min={1}
              className="mod-page__input"
              value={companyIdInput}
              onChange={(e) => setCompanyIdInput(e.target.value)}
              placeholder={t("runs.list.companyIdPlaceholder")}
            />
          </label>
          <button type="submit" className="mod-page__btn mod-page__btn--primary">
            {t("common:actions.apply")}
          </button>
          <button type="button" className="mod-page__btn mod-page__btn--ghost" onClick={resetFilters}>
            {t("shared.reset")}
          </button>
        </form>

        {isLoading ? <div className="mod-page__loading">{t("runs.list.loading")}</div> : null}
        {error ? <div className="mod-page__error">{t("runs.list.loadError")}</div> : null}
        {!isLoading && runs.length === 0 ? <div className="mod-page__empty">{t("runs.list.empty")}</div> : null}

        {runs.length > 0 && (
          <div className="moderator-matching-runs__table-wrap">
            <table className="moderator-matching-runs__table">
              <thead>
                <tr>
                  <th>{t("runs.list.table.run")}</th>
                  <th>{t("runs.list.table.requester")}</th>
                  <th>{t("runs.list.table.status")}</th>
                  <th>{t("runs.list.table.lastStage")}</th>
                  <th>{t("runs.list.table.events")}</th>
                  <th>{t("runs.list.table.duration")}</th>
                  <th>{t("runs.list.table.created")}</th>
                  <th aria-label={t("runs.list.table.actions")} />
                </tr>
              </thead>
              <tbody>
                {runs.map((run) => (
                  <tr key={run.matchRequestId}>
                    <td>
                      <div className="moderator-matching-runs__cell-name">
                        {run.requirementTitle || t("runs.list.matchRequestFallback", { id: run.matchRequestId })}
                      </div>
                      <div className="moderator-matching-runs__cell-sub">#{run.matchRequestId}</div>
                    </td>
                    <td>
                      <div className="moderator-matching-runs__cell-name">{run.requesterCompanyName || "—"}</div>
                      {run.requesterCompanyId != null ? (
                        <div className="moderator-matching-runs__cell-sub">
                          {t("runs.list.companyRef", { id: run.requesterCompanyId })}
                        </div>
                      ) : null}
                    </td>
                    <td>
                      <span
                        className={`moderator-matching-runs__status moderator-matching-runs__status--${run.status}`}
                      >
                        {run.status}
                      </span>
                      {run.failureReason ? (
                        <div className="moderator-matching-runs__cell-sub" title={run.failureReason}>
                          {run.failureReason}
                        </div>
                      ) : null}
                    </td>
                    <td>{run.lastStage ? stageLabel(run.lastStage) : EMPTY_VALUE}</td>
                    <td>{run.eventCount}</td>
                    <td>{fmtDuration(run.totalDurationMs)}</td>
                    <td>{fmtDate(run.createdAt)}</td>
                    <td className="moderator-matching-runs__cell-actions">
                      <Link
                        to={`/moderator/matching/runs/${run.matchRequestId}`}
                        className="mod-page__btn mod-page__btn--ghost"
                      >
                        {t("runs.list.detail")} <i className="fa-solid fa-arrow-right" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

interface StatsCardProps {
  title: string;
  total: number | undefined;
  successful: number | undefined;
  failed: number | undefined;
  avgMs: number | null | undefined;
}

const StatsCard = ({ title, total, successful, failed, avgMs }: StatsCardProps) => {
  const { t } = useTranslation("deals");
  return (
    <div className="moderator-matching-runs__stats-card">
      <div className="moderator-matching-runs__stats-title">{title}</div>
      <div className="moderator-matching-runs__stats-row">
        <Stat label={t("runs.activity.runs")} value={total ?? 0} />
        <Stat label={t("runs.activity.successful")} value={successful ?? 0} tone="ok" />
        <Stat label={t("runs.activity.failed")} value={failed ?? 0} tone={(failed ?? 0) > 0 ? "warn" : "muted"} />
        <Stat label={t("runs.activity.avgDuration")} value={fmtDuration(avgMs ?? null)} />
      </div>
    </div>
  );
};

const Stat = ({ label, value, tone }: { label: string; value: string | number; tone?: "ok" | "warn" | "muted" }) => (
  <div className={`moderator-matching-runs__stat moderator-matching-runs__stat--${tone || "default"}`}>
    <div className="moderator-matching-runs__stat-value">{value}</div>
    <div className="moderator-matching-runs__stat-label">{label}</div>
  </div>
);

export default ModeratorMatchingRunsPage;
